import { EditOutlined, InfoCircleOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client/react';
import { css } from '@emotion/css';
import { App, Button, Table, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import type React from 'react';
import { memo, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useShallow } from 'zustand/react/shallow';
import { RequestStatusBadge } from '../../../components/RequestStatusBadge.tsx';
import { showParamWarning } from '../../../components/showParamWarning';
import { TableList } from '../../../components/TableList.tsx';
import type { PatientDataInput } from '../../../graphql/generated/graphql.ts';
import {
  type ParameterSelection_RequestableParameterFragment,
  type ParameterToAddInput,
  type PatientRequestsQuery,
  RequestStatus,
} from '../../../graphql/generated/graphql.ts';
import { useAppStore } from '../../../hooks/store/useAppStore';
import { useCurrentContextStore } from '../../../hooks/store/useCurrentContextStore.ts';
import { tableActionCell, tableHoverPointer } from '../../../styles/globalCss';
import { SHORT_DATETIME_FORMAT } from '../../../utils/dateFormatUtils';
import { stripTypename } from '../../../utils/helpers';
import { hasFrontDeskRole } from '../../../utils/user.ts';
import { RequestDetailsModal } from '../RequestDetailsModal';
import { PATIENT_REQUESTS_QUERY } from './Requests.gql';

type PatientRequests = NonNullable<PatientRequestsQuery['patientRequests']>[number];

const Requests: React.FC<{
  params: readonly ParameterSelection_RequestableParameterFragment[];
  onAdd: (inputs: ParameterToAddInput[]) => void;
  onUpdate: (requestId: string) => void;
  onReorder: (requestId: string, comment: string, patientData: PatientDataInput) => void;
  processing: boolean;
  reordersOnly: boolean;
  flipParamNames: boolean;
}> = ({ params, onAdd, onReorder, onUpdate, processing, reordersOnly, flipParamNames }) => {
  const { notification } = App.useApp();
  const auth = useAuth();
  const { patientData, requestIdForReorder, requestIdForUpdate } = useAppStore(
    useShallow(state => ({
      patientData: state.patientData,
      requestIdForReorder: state.requestIdForReorder,
      requestIdForUpdate: state.requestIdForUpdate,
    }))
  );
  const { currentDoctorId } = useCurrentContextStore();
  const isFrontDesk = hasFrontDeskRole(auth.user);
  const [detailsRequestId, setDetailsRequestId] = useState<string | null>(null);

  const { data, loading } = useQuery(PATIENT_REQUESTS_QUERY, {
    variables: {
      doctorId: currentDoctorId,
      svnr: patientData?.svnr ?? '',
    },
    skip: !patientData?.svnr,
  });
  const filteredRequests = (data?.patientRequests ?? []).filter(it => {
    if (!reordersOnly) {
      return true;
    }
    return it.reorderable;
  });

  const editAvailable = !requestIdForUpdate;
  const reorderAvailable = !requestIdForReorder;

  const columns: ColumnsType<PatientRequests> = [
    {
      title: 'Erstellt am',
      dataIndex: 'createdAt',
      key: 'createdAt',
      defaultSortOrder: 'descend',
      width: 100,
      ellipsis: true,
      sorter: (a, b) => a.createdAt.localeCompare(b.createdAt),
      render: value => (
        <span
          className={css`
            font-variant-numeric: tabular-nums;
          `}
        >
          {dayjs(value).format(SHORT_DATETIME_FORMAT)}
        </span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      ellipsis: true,
      sorter: (a, b) => a.status.localeCompare(b.status),
      render: value => <RequestStatusBadge status={value} />,
    },
    {
      title: 'Parameter',
      key: 'parameter',
      width: 100,
      ellipsis: true,
      render: (_, record) => (
        <TableList
          maxEntries={3}
          mono={!flipParamNames}
          entries={record.orders
            .flatMap(o => o.orderParameters)
            .sort((a, b) => (flipParamNames ? a.longName.localeCompare(b.longName) : a.shortName.localeCompare(b.shortName)))
            .map(it => (flipParamNames ? it.longName : it.shortName))}
        />
      ),
    },
    {
      title: '',
      key: 'actions',
      fixed: 'right',
      align: 'right',
      ellipsis: true,
      width: '50px',
      className: tableActionCell,
      render: (_, record) => {
        return (
          <>
            {record.reorderable && reorderAvailable && (
              <Tooltip
                title={<span>bis {dayjs(record.reorderableUntil ?? undefined).format(SHORT_DATETIME_FORMAT)}</span>}
                placement="left"
              >
                <Button
                  type="link"
                  icon={<PlusCircleOutlined />}
                  onClick={e => {
                    e.stopPropagation();
                    const patientData = stripTypename(record.patientData);
                    if (patientData.insuredPerson) {
                      patientData.insuredPerson = stripTypename(patientData.insuredPerson);
                    }
                    onReorder(record.id, record.comment, patientData);
                  }}
                >
                  Nachforderung
                </Button>
              </Tooltip>
            )}
            {(record.status === RequestStatus.NEW || record.status === RequestStatus.PRINTED) &&
              editAvailable &&
              ((record.createdByFrontDesk && isFrontDesk) || (!record.createdByFrontDesk && !isFrontDesk)) && (
                <Button
                  type="link"
                  icon={<EditOutlined />}
                  onClick={e => {
                    e.stopPropagation();
                    onUpdate(record.id);
                  }}
                >
                  Bearbeiten
                </Button>
              )}
            <Button
              type="text"
              icon={<InfoCircleOutlined />}
              onClick={e => {
                e.stopPropagation();
                setDetailsRequestId(record.id);
              }}
            />
          </>
        );
      },
    },
  ];

  return (
    <>
      <Table<PatientRequests>
        scroll={{ x: 'max-content' }}
        showSorterTooltip={false}
        rowKey={record => record.id}
        size="small"
        sticky
        className={css`
          overflow-y: auto;
        `}
        dataSource={filteredRequests}
        pagination={false}
        loading={loading || processing}
        columns={columns}
        onRow={record => ({
          onClick: () => {
            if (reordersOnly) {
              return;
            }
            const inputs: ParameterToAddInput[] = [];
            const unavailableParams: string[] = [];
            record.orders.forEach(order => {
              order.orderParameters.forEach(param => {
                const reqParam = params.find(it => it.lab.id === order.lab.id && it.shortName === param.shortName);
                if (reqParam) {
                  inputs.push({
                    parameterId: reqParam.id,
                    preferredBillingType: param.billingType,
                    preferredDiagnoseIds: [],
                  });
                } else {
                  unavailableParams.push(flipParamNames ? param.longName : param.shortName);
                }
              });
            });

            if (unavailableParams.length) {
              showParamWarning(
                notification,
                'Parameter nicht verfügbar',
                'Folgende Parameter stehen nicht zur Verfügung:',
                unavailableParams
              );
            }

            onAdd(inputs);
          },
        })}
        rowClassName={reordersOnly ? '' : tableHoverPointer}
      />
      <RequestDetailsModal requestId={detailsRequestId} onClose={() => setDetailsRequestId(null)} />
    </>
  );
};

export const MemoRequests = memo(Requests);
